import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useMobileDetector } from '../utils/deviceDetection';

// Expandable accordion FAQ item for mobile
const MobileFaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="bg-gradient-to-br from-surface to-gray-800 rounded-lg border border-gray-700 shadow-lg mb-4 overflow-hidden">
      <button 
        className="w-full p-4 text-left flex justify-between items-center text-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-semibold">{question}</h3>
        {isOpen ? <FaChevronUp className="text-blue-400" /> : <FaChevronDown className="text-blue-400" />}
      </button>
      
      {isOpen && (
        <div className="p-4 pt-0 border-t border-gray-700 text-blue-100">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

// Static FAQ item for desktop
const FaqItem = ({ question, answer }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 rounded-lg p-6 border border-gray-700 shadow-lg">
    <h3 className="text-xl font-semibold text-white mb-3">{question}</h3>
    <p className="text-blue-100">{answer}</p>
  </div>
);

const FAQ = () => {
  const isMobile = useMobileDetector();
  const faqs = [
    {
      question: "What is AUTOLAWN?",
      answer: "AUTOLAWN is a comprehensive lawn care business management platform that helps you manage customers, schedule jobs, track payments, and grow your business."
    },
    {
      question: "How do I get started?",
      answer: "Simply sign up for an account, choose your subscription plan, and you can start managing your lawn care business right away. We offer a 14-day free trial on our Basic plan."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards through our secure payment processor, Stripe."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees."
    },
    {
      question: "Do you offer customer support?",
      answer: "Yes, we offer email support on all plans. Our Pro and Enterprise plans include priority support, and Enterprise subscribers receive phone support as well."
    },
    {
      question: "Is my data secure?",
      answer: "We take data security very seriously. All data is encrypted in transit and at rest. We use industry-standard security practices to keep your information safe."
    },
    {
      question: "Can I access AUTOLAWN on mobile devices?",
      answer: "Yes, AUTOLAWN is fully responsive and works on smartphones and tablets. We're also developing dedicated mobile apps that will be released soon."
    },
    {
      question: "How many team members can I add?",
      answer: "You can add unlimited team members on all of our plans. Each team member can have their own login credentials and permission levels."
    }
  ];

  return (
    <div className="bg-gradient-to-b from-gray-900 to-background text-text min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-12 md:mb-16 relative">
          <div className="absolute inset-0 blur-[100px] bg-gradient-to-r from-blue-500/30 to-purple-500/30 transform rotate-12"></div>
          <h1 className="relative text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            Frequently Asked Questions
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto relative">
            Find answers to common questions about AUTOLAWN
          </p>
        </div>

        <div className={`max-w-3xl mx-auto ${isMobile ? '' : 'space-y-6'}`}>
          {faqs.map((faq, index) => (
            isMobile 
              ? <MobileFaqItem key={index} {...faq} />
              : <FaqItem key={index} {...faq} />
          ))}
        </div>
        
        {/* Quick contact section */}
        <div className="text-center mt-12 md:mt-16">
          <p className="text-blue-100 mb-4">
            Still have questions? We're here to help!
          </p>
          <a 
            href="mailto:support@autolawn.app" 
            className={`inline-block px-5 py-2.5 md:px-8 md:py-3 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium shadow-lg hover:from-blue-600 hover:to-blue-700 transition duration-200`}
          >
            Contact Support
          </a>
        </div>
      </main>
    </div>
  );
};

export default FAQ; 
